import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import AnimatedModal from "./AnimatedModal";
import ConfirmationModal from "./ConfirmationModal";
import { documentAPI } from "../utils/api";
import { jwtUtils } from "../utils/jwtUtils";
import { useAuth } from "../contexts/AuthContext";
import { JsonView, darkStyles, defaultStyles } from 'react-json-view-lite';
import 'react-json-view-lite/dist/index.css';
import { useTranslation } from "../contexts/TranslationContext";

export default function FilesModal({ isOpen, onClose, onDocumentChange, onStartPdfProgress, pdfProgress }) {
  const { t } = useTranslation();
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isDragOver, setIsDragOver] = useState(false);
  const [metadata, setMetadata] = useState([]);
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);
  const [showMetadata, setShowMetadata] = useState(false);
  const [clausesData, setClausesData] = useState(null);
  const [isLoadingClausesData, setIsLoadingClausesData] = useState(false);
  const [showJsonViewer, setShowJsonViewer] = useState(false);
  const [isProcessingEmbeddings, setIsProcessingEmbeddings] = useState(false);
  const [embeddingsProgress, setEmbeddingsProgress] = useState(null);
  const [processingMode, setProcessingMode] = useState('balanced');
  const [showProcessingDropdown, setShowProcessingDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [showUploadLimitModal, setShowUploadLimitModal] = useState(false);
  const [uploadLimitContext, setUploadLimitContext] = useState(null);

  // Ref for the process section to enable auto-scroll
  const processSectionRef = useRef(null);
  const dropdownRef = useRef(null);
  const sseActiveForDocRef = useRef(false);

  const { user, isAuthenticated } = useAuth();
  
  // Check if current user is admin - now reactive to auth state
  const isAdmin = user && (user.role === 'admin' || user.role === 'super_admin' || user.roles?.includes('admin') || user.roles?.includes('super_admin') || user.user_role === 'admin' || user.user_role === 'super_admin');
  const userInfo = user ? {
    id: user.id || user.sub,
    email: user.email,
    name: user.name || user.username,
    role: user.role || user.roles || user.user_role,
    isAdmin: isAdmin
  } : null;

  // Load documents when modal opens
  useEffect(() => {
    if (isOpen) {
      loadDocuments();
      // Don't load metadata on open - wait for document selection
      setMetadata([]);
      setSelectedDocument(null);
    }
  }, [isOpen]);

  // React to SSE pdfProgress updates to finish UI flow without awaiting the POST
  useEffect(() => {
    if (!pdfProgress || !selectedDocument) return;
    if (pdfProgress.documentId !== selectedDocument.id) return;

    // Track whether SSE is actively reporting for this document
    if (pdfProgress.status === 'running' || pdfProgress.status === 'reconnecting') {
      sseActiveForDocRef.current = true;
    }
    if (pdfProgress.status === 'completed' || pdfProgress.status === 'error') {
      sseActiveForDocRef.current = false;
    }

    if (pdfProgress.status === 'completed') {
      setIsProcessing(false);
      setEmbeddingsProgress(null);
      setSuccessMessage(`Processing completed for: ${selectedDocument.originalName} - Mode: ${processingMode}`);
      // Refresh metadata and notify parent
      loadMetadata(selectedDocument.id);
      if (onDocumentChange) {
        onDocumentChange();
      }
    }
    if (pdfProgress.status === 'error') {
      setIsProcessing(false);
      setEmbeddingsProgress(null);
      setError('Processing failed. Please try again.');
    }
  }, [pdfProgress, selectedDocument, processingMode]);

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside both the dropdown button and the dropdown menu
      const isOutsideButton = dropdownRef.current && !dropdownRef.current.contains(event.target);
      const isOutsideDropdown = !event.target.closest('[data-dropdown-menu]');
      
      if (isOutsideButton && isOutsideDropdown) {
        setShowProcessingDropdown(false);
      }
    };

    if (showProcessingDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProcessingDropdown]);

  const loadDocuments = async () => {
    setIsLoading(true);
    setError("");
    
    try {
      console.log('🔄 Loading documents with authentication...');
      const response = await documentAPI.getDocuments();
      setDocuments(response);
    } catch (error) {
      console.error("Failed to load documents:", error);
      if (error.message.includes('401')) {
        setError("Authentication required. Please log in to access your documents.");
      } else {
        setError("Failed to load documents. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadMetadata = async (documentId = null) => {
    // If no documentId provided and no selected document, return early
    if (!documentId && !selectedDocument) {
      setMetadata([]);
      return;
    }
    
    const targetDocumentId = documentId || selectedDocument.id;
    
    setIsLoadingMetadata(true);
    setError("");
    
    try {
      console.log('🔄 Loading metadata record (lightweight) for document:', targetDocumentId);
      const metadataItem = await documentAPI.getMetadataRecord(targetDocumentId);
      // Wrap single document metadata in array for consistent UI handling
      setMetadata([metadataItem]);
    } catch (error) {
      console.error("Failed to load metadata:", error);
      if (error.message.includes('401')) {
        setError("Authentication required. Please log in to access metadata.");
      } else {
        setError("Failed to load metadata. Please try again.");
      }
      setMetadata([]);
    } finally {
      setIsLoadingMetadata(false);
    }
  };

  const loadClausesData = async (documentId = null) => {
    console.log('🟡 loadClausesData called with:', {
      documentId: documentId,
      selectedDocumentId: selectedDocument?.id,
      targetWillBe: documentId || selectedDocument?.id
    });
    
    const targetDocumentId = documentId || selectedDocument?.id;
    
    if (!targetDocumentId) {
      console.log('🔴 No documentId available, setting clausesData to null');
      setClausesData(null);
      return;
    }
    
    console.log('🟢 Starting clauses data loading for documentId:', targetDocumentId);
    setIsLoadingClausesData(true);
    setError("");
    
    try {
      console.log('🔄 Loading clauses data (heavy) for document:', targetDocumentId);
      // Use heavy endpoint to include metadata + clauses
      const response = await documentAPI.getMetadata(targetDocumentId);
      console.log('📊 Received clauses/metadata response:', response);

      // Extract only the clausesData portion if present; otherwise use response as-is
      const onlyClauses =
        response?.metadata?.clausesData ??
        response?.clausesData ??
        response;

      console.log('🟢 Setting ONLY clausesData and opening JSON viewer');
      setClausesData(onlyClauses);
      setShowJsonViewer(true);
      console.log('✅ JSON viewer visible');
    } catch (error) {
      console.error("Failed to load clauses data:", error);
      if (error.message.includes('401')) {
        setError("Authentication required. Please log in to access clauses data.");
      } else {
        setError("Failed to load clauses data. Please try again.");
      }
      setClausesData(null);
    } finally {
      setIsLoadingClausesData(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check admin permissions
    if (!isAdmin) {
      setError("Only administrators can upload documents. Please contact your system administrator.");
      return;
    }

    await uploadFile(file);
    
    // Clear the file input
    e.target.value = "";
  };

  const uploadFile = async (file) => {
    // Double-check admin permissions
    if (!isAdmin) {
      setError("Only administrators can upload documents.");
      return;
    }

    if (file.type !== "application/pdf") {
      setError("Only PDF files are allowed.");
      return;
    }

    setIsUploading(true);
    setError("");
    setSuccessMessage("");

    try {
      console.log('🔄 Uploading document with authentication...');
      
      // Debug: Check authentication state
      const token = localStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('refreshToken');
      const userData = localStorage.getItem('userData');
      
      console.log('🔍 Authentication Debug:', {
        hasToken: !!token,
        tokenLength: token ? token.length : 0,
        tokenStart: token ? token.substring(0, 20) + '...' : 'none',
        hasRefreshToken: !!refreshToken,
        hasUserData: !!userData,
        isAdmin,
        localStorageKeys: Object.keys(localStorage)
      });
      
      const response = await documentAPI.upload(file);
      console.log("File uploaded successfully:", response);
      
      // Add the uploaded document to the list
      setDocuments((prev) => [response, ...prev]);
      setSuccessMessage(`"${response.originalName}" uploaded successfully!`);
      
      // Notify parent component about document change
      if (onDocumentChange) {
        onDocumentChange();
      }
    } catch (error) {
      console.error("Upload failed:", error);
      // Gracefully handle specific quota/limit responses
      const serverError = (error?.data?.error || error?.message || '').toLowerCase();
      if (serverError.includes('document limit') || serverError.includes('limit reached')) {
        setUploadLimitContext(error?.data?.context || null);
        setShowUploadLimitModal(true);
      } else if (error.message.includes('401')) {
        setError("Authentication required. Please log in to upload documents.");
      } else {
        setError("Failed to upload file. Please try again.");
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteClick = (document, e) => {
    e.stopPropagation(); // Prevent document selection when clicking delete
    
    // Check admin permissions
    if (!isAdmin) {
      setError("Only administrators can delete documents. Please contact your system administrator.");
      return;
    }
    
    setDocumentToDelete(document);
    setShowDeleteConfirmation(true);
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;
    
    // Double-check admin permissions
    if (!isAdmin) {
      setError("Only administrators can delete documents.");
      setShowDeleteConfirmation(false);
      setDocumentToDelete(null);
      return;
    }
    
    try {
      await documentAPI.deleteDocument(documentToDelete.id);
      setDocuments((prev) => prev.filter(doc => doc.id !== documentToDelete.id));
      
      // If the deleted document was selected, clear the selection
      if (selectedDocument?.id === documentToDelete.id) {
        setSelectedDocument(null);
      }
      
      setSuccessMessage(`"${documentToDelete.originalName}" deleted successfully.`);
      
      // Notify parent component about document change
      if (onDocumentChange) {
        onDocumentChange();
      }
    } catch (error) {
      console.error("Delete failed:", error);
      if (error.message.includes('401')) {
        setError("Authentication required. Please log in to delete documents.");
      } else {
        setError("Failed to delete document. Please try again.");
      }
    } finally {
      setShowDeleteConfirmation(false);
      setDocumentToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirmation(false);
    setDocumentToDelete(null);
  };

  const handleProcessingModeSelect = (mode) => {
    console.log('Processing mode selected:', mode);
    setProcessingMode(mode);
    setShowProcessingDropdown(false);
  };

  const toggleProcessingDropdown = () => {
    if (!showProcessingDropdown && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width
      });
    }
    setShowProcessingDropdown(!showProcessingDropdown);
  };

  const handleProcess = async () => {
    console.log("Process button clicked");
    console.log("Selected document at process:", selectedDocument);
    console.log("Processing mode:", processingMode);
    if (!isAdmin) {
      setError("Only administrators can process documents. Please contact your system administrator.");
      return;
    }
    if (!selectedDocument) {
      setError("Please select a PDF document first.");
      return;
    }

    setIsProcessing(true);
    setError("");
    setSuccessMessage("");
    // Start global SSE progress (visible on TopBar) as soon as process starts
    if (typeof onStartPdfProgress === 'function') {
      try { onStartPdfProgress(selectedDocument.id, selectedDocument.originalName, processingMode); } catch (e) {}
    }

    // Fire-and-forget trigger for backend processing. Do not await long-running response
    // to avoid 504 Gateway Time-out. Progress and completion will be handled via SSE.
    try {
      setEmbeddingsProgress("Processing document for clauses...");
      console.log("Triggering documentAPI.process (non-blocking) with:", selectedDocument.id, {
        analysisType: "technical_compliance",
        standards: ["AS3000"],
        modelType: processingMode
      });

      // Kick off without awaiting, but catch/log eventual rejection
      documentAPI
        .process(selectedDocument.id, {
          analysisType: "technical_compliance",
          standards: ["AS3000"],
          modelType: processingMode
        })
        .then((response) => {
          console.log("documentAPI.process responded:", response);
        })
        .catch((err) => {
          // If SSE is not active for this document, notify backend to cancel
          const isSseActive = sseActiveForDocRef.current === true;
          console.warn("documentAPI.process error:", err?.status || err?.message || err, "| SSE active:", isSseActive);
          if (!isSseActive) {
            try {
              documentAPI
                .cancelProcess(selectedDocument.id, {
                  reason: 'trigger_failed',
                  error: {
                    status: err?.status || null,
                    message: err?.message || 'Unknown error',
                  },
                })
                .catch(() => {});
            } catch (_) {}
          }
        });
    } catch (err) {
      console.error("Failed to trigger processing:", err);
      try {
        if (selectedDocument?.id) {
          documentAPI
            .cancelProcess(selectedDocument.id, {
              reason: 'trigger_exception',
              error: { message: err?.message || 'Unknown error' },
            })
            .catch(() => {});
        }
      } catch (_) {}
      setError("Failed to start processing. Please try again.");
      setIsProcessing(false);
      setEmbeddingsProgress(null);
      return;
    }
  };

  const handleDeleteMetadata = async (metadataId) => {
    if (!isAdmin) {
      setError("Only administrators can delete metadata. Please contact your system administrator.");
      return;
    }

    try {
      console.log('🗑️ Deleting metadata:', metadataId);
      await documentAPI.deleteMetadata(metadataId);
      setSuccessMessage("Metadata deleted successfully");
      // Reload metadata list
      loadMetadata();
    } catch (error) {
      console.error("Failed to delete metadata:", error);
      setError("Failed to delete metadata. Please try again.");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    // Only show drag feedback if user is admin
    if (isAdmin) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    // Check admin permissions before processing drop
    if (!isAdmin) {
      setError("Only administrators can upload documents. Please contact your system administrator.");
      return;
    }
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      uploadFile(files[0]);
    }
  };

  // Handle document selection with auto-scroll to process section
  const handleDocumentSelect = (document) => {
    setSelectedDocument(document);
    
    // Load metadata for the selected document
    loadMetadata(document.id);
    
    // Auto-scroll to process section after a short delay to allow rendering
    setTimeout(() => {
      if (processSectionRef.current) {
        processSectionRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest'
        });
      }
    }, 100);
  };

  // Debug: Add state debugging to window object for browser console access
  React.useEffect(() => {
    window.filesModalDebug = {
      showJsonViewer,
      clausesData,
      clausesDataExists: !!clausesData,
      shouldRender: showJsonViewer && clausesData,
      isLoadingClausesData,
      metadata,
      selectedDocument,
      error,
      isProcessingEmbeddings,
      embeddingsProgress
    };
    console.log('🔧 FilesModal Debug State Updated:', window.filesModalDebug);
  }, [showJsonViewer, clausesData, isLoadingClausesData, metadata, selectedDocument, error, isProcessingEmbeddings, embeddingsProgress]);

  return (
    <>
    <AnimatedModal isOpen={isOpen} onClose={onClose} className="w-full max-w-2xl md:max-w-3xl lg:max-w-4xl max-h-[90vh]">
      <div className="bg-gradient-to-br from-white via-gray-50/50 to-indigo-50/30 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 rounded-2xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 backdrop-blur-sm relative overflow-hidden flex flex-col max-h-[90vh] w-full">
        
        {/* Background Elements */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-24 h-24 bg-indigo-500/5 rounded-full animate-pulse blur-2xl" style={{animationDuration: '8s'}}></div>
          <div className="absolute bottom-1/4 right-1/4 w-20 h-20 bg-purple-500/5 rounded-full animate-pulse blur-2xl" style={{animationDuration: '10s', animationDelay: '2s'}}></div>
        </div>

        {/* Fixed Header */}
        <div className="relative z-10 flex-shrink-0 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('documents.title')}</h2>
            </div>
            <button 
              onClick={onClose} 
              className="btn-icon"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="relative z-10 flex-1 overflow-y-auto px-4 sm:px-6 md:px-8 pb-6 md:pb-8 modal-scrollable">
          <div className="space-y-6">
            {/* Upload Section */}
            <div className="card-primary p-4 sm:p-5">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3 flex items-center gap-2">
                <div className={`w-4 h-4 rounded-md flex items-center justify-center ${
                  isAdmin ? 'bg-green-500' : 'bg-gray-400'
                }`}>
                  <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                </div>
                {t('documents.uploadNew')}
                {!isAdmin && (
                  <span className="text-xs bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 px-2 py-1 rounded-md">
                    {t('documents.adminOnly')}
                  </span>
                )}
              </h3>
              {!isAdmin ? (
                <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-xl p-4 text-center">
                  <div className="flex items-center justify-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-amber-100 dark:bg-amber-800 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                  </div>
                   <h4 className="font-semibold text-amber-800 dark:text-amber-200 text-sm mb-2">{t('documents.adminRequired')}</h4>
                   <p className="text-amber-700 dark:text-amber-300 text-xs leading-relaxed">{t('documents.adminDescription')}</p>
                  {userInfo && (
                    <div className="mt-3 text-xs text-amber-600 dark:text-amber-400">
                      Current role: <span className="font-medium">{userInfo.role || 'User'}</span>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="flex gap-3">
                    <div className="flex-1 relative">
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={handleUpload}
                        disabled={isUploading || !isAdmin}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed z-10"
                        id="file-upload"
                      />
                      <label 
                        htmlFor="file-upload" 
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        className={`flex items-center justify-center gap-3 px-6 py-4 border-2 border-dashed rounded-xl text-sm font-medium transition-all duration-300 cursor-pointer min-h-[80px] simple-hover-lift ${
                          isUploading 
                            ? 'border-gray-300 bg-gray-50 text-gray-400 cursor-not-allowed' 
                            : isDragOver
                            ? 'border-indigo-500 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 scale-[1.02] shadow-lg'
                            : 'border-indigo-300 dark:border-indigo-600 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 text-indigo-700 dark:text-indigo-300 hover:border-indigo-400 hover:bg-gradient-to-br hover:from-indigo-100 hover:to-purple-100 dark:hover:from-indigo-900/30 dark:hover:to-purple-900/30 hover:shadow-md'
                        }`}
                      >
                        <div className="flex flex-col items-center gap-2">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                            isUploading 
                              ? 'bg-gray-200 dark:bg-gray-600'
                              : isDragOver
                              ? 'bg-indigo-500 shadow-lg scale-110'
                              : 'bg-gradient-to-br from-indigo-500 to-purple-600 hover:shadow-md'
                          }`}>
                            {isUploading ? (
                              <div className="w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                              </svg>
                            )}
                          </div>
                          <div className="text-center">
                            <div className="font-semibold">
                              {isUploading ? 'Uploading...' : isDragOver ? 'Drop PDF Here' : 'Choose PDF File'}
                            </div>
                            <div className="text-xs opacity-70 mt-1">
                              {isUploading ? t('common.loading') : t('documents.dragDrop')}
                            </div>
                          </div>
                        </div>
                      </label>
                    </div>
                    {isUploading && (
                      <div className="px-4 py-3 bg-gradient-to-r from-indigo-100 to-purple-100 dark:from-indigo-900/50 dark:to-purple-900/50 text-indigo-700 dark:text-indigo-300 rounded-xl text-sm font-medium flex items-center gap-2 animate-pulse">
                        <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                        Processing...
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Messages */}
            {error && (
              <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 rounded-xl p-4 text-sm animate-fade-in">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  {error}
                </div>
              </div>
            )}

            {successMessage && (
              <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 text-green-700 dark:text-green-300 rounded-xl p-4 text-sm animate-fade-in">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {successMessage}
                </div>
              </div>
            )}

            {/* Document List Section */}
            <div className="card-primary p-5">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-4 flex items-center gap-2">
                <div className="w-4 h-4 bg-blue-500 rounded-md flex items-center justify-center">
                  <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                {t('documents.yourDocuments')} ({documents.length})
                {isLoading && (
                  <div className="ml-2 w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                )}
              </h3>
              
              {isLoading ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                    <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
                </div>
              ) : documents.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('documents.noDocuments')}</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{t('documents.uploadToStart')}</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-80 overflow-y-auto modal-scrollable">
                  {documents.map((document) => (
                    <div
                      key={document.id}
                      onClick={() => handleDocumentSelect(document)}
                      className={`cursor-pointer p-4 rounded-xl transition-all duration-300 group ${
                        selectedDocument?.id === document.id
                          ? "border-2 border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300"
                          : "card-primary card-hover text-gray-800 dark:text-gray-200"
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            selectedDocument?.id === document.id 
                              ? 'bg-white/20' 
                              : 'bg-red-500'
                          }`}>
                            <span className={`text-xs font-bold ${
                              selectedDocument?.id === document.id 
                                ? 'text-white' 
                                : 'text-white'
                            }`}>PDF</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold truncate text-sm">{document.originalName}</h4>
                            <p className={`text-xs mt-1 ${
                              selectedDocument?.id === document.id 
                                ? 'text-indigo-600 dark:text-indigo-200' 
                                : 'text-gray-500 dark:text-gray-400'
                            }`}>
                              Uploaded: {formatDate(document.uploadDate)}
                            </p>
                            <p className={`text-xs ${
                              selectedDocument?.id === document.id 
                                ? 'text-indigo-600 dark:text-indigo-200' 
                                : 'text-gray-500 dark:text-gray-400'
                            }`}>
                              ID: {document.id.slice(-8)}...
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={(e) => handleDeleteClick(document, e)}
                          disabled={!isAdmin}
                          className={`ml-3 w-8 h-8 flex items-center justify-center rounded-lg text-sm font-bold transition-all duration-300 flex-shrink-0 ${
                            !isAdmin
                              ? "opacity-30 cursor-not-allowed text-gray-300 dark:text-gray-600"
                              : selectedDocument?.id === document.id
                              ? "hover:bg-red-500/20 text-white opacity-0 group-hover:opacity-100"
                              : "hover:bg-red-500 hover:text-white text-gray-500 dark:text-gray-400 opacity-0 group-hover:opacity-100"
                          }`}
                          title={!isAdmin ? "Admin access required" : "Delete document"}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            {selectedDocument && (
              <>
                {console.log("Rendering process button, selectedDocument:", selectedDocument)}
                <div ref={processSectionRef} className="card-primary p-5">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3 flex items-center gap-2">
                    <div className="w-4 h-4 bg-purple-500 rounded-md flex items-center justify-center">
                      <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                      </svg>
                    </div>
                    Process Selected Document
                  </h3>
                  <div className="flex items-start gap-3 mb-4 p-3 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg">
                    <div className="w-8 h-8 bg-red-500 rounded-md flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs font-bold">PDF</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 truncate">{selectedDocument.originalName}</h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Uploaded: {formatDate(selectedDocument.uploadDate)}
                      </p>
                    </div>
                  </div>
                  {/* In-modal progress panel */}
                  {pdfProgress && pdfProgress.documentId === selectedDocument.id && (
                    <div className="mb-4 p-3 rounded-lg bg-white dark:bg-gray-800 border border-indigo-200/60 dark:border-indigo-700/60 shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 text-sm font-semibold text-indigo-700 dark:text-indigo-300">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" />
                          </svg>
                          {pdfProgress.step || 'Processing'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{typeof pdfProgress.percent === 'number' ? `${Math.round(pdfProgress.percent)}%` : ''}</div>
                      </div>
                      <div className="w-full h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 transition-all duration-300`}
                          style={{ width: `${Math.max(0, Math.min(100, pdfProgress.percent || 0))}%` }}
                        ></div>
                      </div>
                      {pdfProgress.status === 'error' && (
                        <div className="mt-2 text-xs text-red-600 dark:text-red-400">An error occurred while processing.</div>
                      )}
                    </div>
                  )}
                  <div className="flex gap-3">
                    {/* Processing Mode Dropdown */}
                    <div className="relative flex-1" ref={dropdownRef}>
                                              <button
                          onClick={toggleProcessingDropdown}
                          disabled={!isAdmin || isProcessing}
                        className={`w-full py-3 px-4 rounded-xl font-semibold transition-all duration-300 border-2 ${
                          !isAdmin
                            ? "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 cursor-not-allowed border-yellow-200 dark:border-yellow-700"
                            : isProcessing
                            ? "bg-gray-200 dark:bg-gray-700 text-gray-500 cursor-not-allowed border-gray-300 dark:border-gray-600"
                            : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-indigo-200 dark:border-indigo-700 hover:border-indigo-300 dark:hover:border-indigo-600 hover:bg-indigo-50 dark:hover:bg-gray-700"
                        }`}
                        title={!isAdmin ? "Only administrators can process documents." : undefined}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${
                              processingMode === 'high' ? 'bg-red-500' :
                              processingMode === 'balanced' ? 'bg-yellow-500' :
                              'bg-green-500'
                            }`}></div>
                            <span className="capitalize">{processingMode}</span>
                          </div>
                          <svg 
                            className={`w-4 h-4 transition-transform duration-200 ${
                              showProcessingDropdown ? 'rotate-180' : ''
                            }`} 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </button>
                      
                      {/* Dropdown Menu */}
                      {showProcessingDropdown && isAdmin && !isProcessing && createPortal(
                        <div 
                          data-dropdown-menu
                          onClick={(e) => e.stopPropagation()}
                          className="fixed bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-[9999] overflow-hidden" 
                          style={{
                            top: dropdownPosition.top,
                            left: dropdownPosition.left,
                            width: dropdownPosition.width || 'auto',
                            minWidth: '200px'
                          }}
                        >
                          {[
                            { 
                              mode: 'fast', 
                              color: 'green', 
                              icon: '⚡', 
                              description: 'Quickest processing, basic extraction and good embeddings.' 
                            },
                            { 
                              mode: 'balanced', 
                              color: 'yellow', 
                              icon: '⚖️', 
                              description: 'Better coverage with smart ML on targeted pages; good speed and quality.' 
                            },
                            { 
                              mode: 'high', 
                              color: 'red', 
                              icon: '🎯', 
                              description: 'Best coverage with more thorough ML; highest quality embeddings.' 
                            }
                          ].map((option) => (
                            <button
                              key={option.mode}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleProcessingModeSelect(option.mode);
                              }}
                              title={option.description}
                              className={`w-full px-4 py-3 text-left hover:bg-indigo-50 dark:hover:bg-gray-700 transition-colors duration-200 ${
                                processingMode === option.mode 
                                  ? 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300' 
                                  : 'text-gray-700 dark:text-gray-300'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-2 h-2 rounded-full bg-${option.color}-500`}></div>
                                <span className="text-lg">{option.icon}</span>
                                <div className="flex-1">
                                  <div className="font-semibold capitalize">{option.mode}</div>
                                </div>
                                {processingMode === option.mode && (
                                  <svg className="w-4 h-4 text-indigo-600 dark:text-indigo-400" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>,
                        document.body
                      )}
                    </div>

                    {/* Process Button */}
                    <button
                      onClick={() => { console.log("Button onClick fired"); handleProcess(); }}
                      disabled={!isAdmin || isProcessing}
                      className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                        !isAdmin
                          ? "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 cursor-not-allowed border border-yellow-200 dark:border-yellow-700"
                          : isProcessing
                          ? "bg-gray-200 dark:bg-gray-700 text-gray-500 cursor-not-allowed"
                          : "bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white shadow-lg shadow-indigo-500/25 simple-hover-lift"
                      }`}
                      title={!isAdmin ? "Only administrators can process documents." : undefined}
                    >
                      {isProcessing ? (
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                          Processing...
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          Process
                        </div>
                      )}
                    </button>
                  </div>
                  
                  {!isAdmin && (
                    <div className="mt-2 text-xs text-yellow-800 dark:text-yellow-200 bg-yellow-50 dark:bg-yellow-900/40 rounded-lg px-3 py-2 border border-yellow-200 dark:border-yellow-700 text-center">
                      <strong>Administrator Access Required</strong><br />
                      Only administrators can process documents. Please contact your system administrator.
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Metadata Section */}
            <div className="card-primary p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-2">
                  <div className="w-4 h-4 bg-emerald-500 rounded-md flex items-center justify-center">
                    <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V8zm0 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2z" clipRule="evenodd" />
                    </svg>
                  </div>
                  Document Clauses & Analysis ({selectedDocument ? '1 document' : '0 documents'})
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => selectedDocument && loadMetadata(selectedDocument.id)}
                    disabled={isLoadingMetadata || !selectedDocument}
                    className="btn-success-sm disabled:opacity-50"
                  >
                    {isLoadingMetadata ? (
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-3 border border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
                        Loading...
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Refresh
                      </div>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      if (!selectedDocument) return;
                      setShowJsonViewer(true);
                      loadClausesData(selectedDocument.id);
                    }}
                    disabled={isLoadingClausesData || !selectedDocument}
                    className="btn-info-sm disabled:opacity-50"
                  >
                    {isLoadingClausesData ? (
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-3 border border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                        Loading...
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                        </svg>
                        View JSON
                      </div>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      const next = !showMetadata;
                      setShowMetadata(next);
                      if (next && selectedDocument && metadata.length === 0 && !isLoadingMetadata) {
                        loadMetadata(selectedDocument.id);
                      }
                    }}
                    className="btn-secondary-sm"
                  >
                    {showMetadata ? 'Hide' : 'Show'} Details
                  </button>
                </div>
              </div>

              {!selectedDocument ? (
                <div className="text-center py-6">
                  <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">
                    Select a document to view its clauses and analysis.
                  </p>
                </div>
              ) : metadata.length === 0 ? (
                <div className="text-center py-6">
                  <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">
                    Process the selected document to generate clauses and analysis.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {metadata.map((item) => (
                    <div key={item._id || item.documentId} className="space-y-4">
                      {/* Document Header */}
                      <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-4 border border-emerald-200/50 dark:border-emerald-700/50">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <h4 className="font-semibold text-emerald-800 dark:text-emerald-300 text-sm">
                              {item.pdfFileName || item.originalName}
                            </h4>
                            <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
                              Processed: {formatDate(item.createdAt || item.processedAt)}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {item.analysisResults?.complianceScore && (
                              <div className={`px-2 py-1 rounded-md text-xs font-medium ${
                                item.analysisResults.complianceScore >= 90 
                                  ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300'
                                  : item.analysisResults.complianceScore >= 70
                                  ? 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300'
                                  : 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300'
                              }`}>
                                Score: {item.analysisResults.complianceScore}%
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Analysis Summary */}
                        {item.analysisResults && (
                          <div className="grid grid-cols-3 gap-3 mt-3 text-xs">
                            <div className="text-center p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                              <div className="font-semibold text-green-700 dark:text-green-300">
                                {item.analysisResults.compliantClauses || 0}
                              </div>
                              <div className="text-green-600 dark:text-green-400">Compliant</div>
                            </div>
                            <div className="text-center p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                              <div className="font-semibold text-yellow-700 dark:text-yellow-300">
                                {item.analysisResults.partiallyCompliantClauses || 0}
                              </div>
                              <div className="text-yellow-600 dark:text-yellow-400">Partial</div>
                            </div>
                            <div className="text-center p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                              <div className="font-semibold text-red-700 dark:text-red-300">
                                {item.analysisResults.nonCompliantClauses || 0}
                              </div>
                              <div className="text-red-600 dark:text-red-400">Issues</div>
                            </div>
                          </div>
                        )}

                        {/* Basic Info from API Response */}
                        {!item.analysisResults && (
                          <div className="mt-3 text-xs space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-emerald-700 dark:text-emerald-300 font-medium">Total Clauses:</span>
                              <span className="text-emerald-600 dark:text-emerald-400">{item.clausesCount || 'N/A'}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-emerald-700 dark:text-emerald-300 font-medium">Metadata ID:</span>
                              <span className="text-emerald-600 dark:text-emerald-400 font-mono text-xs">{item.metadataId || 'N/A'}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-emerald-700 dark:text-emerald-300 font-medium">Status:</span>
                              <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded text-xs">
                                Processed
                              </span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Clauses List */}
                      {showMetadata && (
                        <div className="space-y-3">
                          {item.extractedClauses && item.extractedClauses.length > 0 ? (
                            <>
                              <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                                Extracted Clauses ({item.extractedClauses.length})
                              </h5>
                              {item.extractedClauses.map((clause) => (
                                <div key={clause.id} className={`rounded-lg p-4 border-l-4 ${
                                  clause.compliance === 'compliant' 
                                    ? 'bg-green-50 dark:bg-green-900/20 border-green-500'
                                    : clause.compliance === 'partially-compliant'
                                    ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-500'
                                    : 'bg-red-50 dark:bg-red-900/20 border-red-500'
                                }`}>
                                  <div className="flex items-start justify-between mb-2">
                                    <h6 className="font-semibold text-sm text-gray-800 dark:text-gray-200">
                                      {clause.title}
                                    </h6>
                                  </div>
                                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                                    {clause.content}
                                  </p>
                                  
                                  {/* Issues and Recommendations */}
                                  {(clause.issues?.length > 0 || clause.recommendations?.length > 0) && (
                                    <div className="mt-3 space-y-2">
                                      {clause.issues?.length > 0 && (
                                        <div>
                                          <span className="text-xs font-medium text-red-700 dark:text-red-300">Issues:</span>
                                          <ul className="mt-1 ml-4 space-y-1">
                                            {clause.issues.map((issue, index) => (
                                              <li key={index} className="text-xs text-red-600 dark:text-red-400 flex items-start gap-1">
                                                <span className="w-1 h-1 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
                                                {issue}
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                      {clause.recommendations?.length > 0 && (
                                        <div>
                                          <span className="text-xs font-medium text-blue-700 dark:text-blue-300">Recommendations:</span>
                                          <ul className="mt-1 ml-4 space-y-1">
                                            {clause.recommendations.map((rec, index) => (
                                              <li key={index} className="text-xs text-blue-600 dark:text-blue-400 flex items-start gap-1">
                                                <span className="w-1 h-1 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                                                {rec}
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </>
                          ) : (
                            <div className="text-center py-6 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                              <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-3">
                                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              </div>
                              <p className="text-gray-500 dark:text-gray-400 text-sm">
                                Clauses file created: {item.clausesCount || 0} clauses processed
                              </p>
                              <p className="text-gray-400 dark:text-gray-500 text-xs mt-1">
                                File path: {item.clausesFilePath ? '.../' + item.clausesFilePath.split('/').pop() : 'N/A'}
                              </p>
                              <button
                                onClick={() => {
                                  console.log('🔵 View Clauses JSON button clicked!', {
                                    itemDocumentId: item.documentId,
                                    item: item,
                                    isLoadingClausesData: isLoadingClausesData
                                  });
                                  setShowJsonViewer(true);
                                  loadClausesData(item.documentId);
                                }}
                                disabled={isLoadingClausesData}
                                className="mt-3 btn-info-sm disabled:opacity-50"
                              >
                                {isLoadingClausesData ? (
                                  <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 border border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                    Loading...
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                                    </svg>
                                    View Clauses JSON
                                  </div>
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Embeddings Progress Indicator - hidden as requested */}
      </div>
    </AnimatedModal>

    {/* Delete Confirmation Modal */}
    <ConfirmationModal
      isOpen={showDeleteConfirmation}
      onClose={handleDeleteCancel}
      onConfirm={handleDeleteConfirm}
      title="Delete Document"
      message={`Are you sure you want to delete "${documentToDelete?.originalName}"? This action cannot be undone.`}
      confirmText="Delete"
      cancelText="Cancel"
    />

    {/* JSON Viewer Modal */}
    {console.log('🔍 JSON Viewer render check:', { showJsonViewer, clausesDataExists: !!clausesData, shouldRender: showJsonViewer && clausesData })}
    {showJsonViewer && clausesData && (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
        <div className="bg-gradient-to-br from-white via-gray-50/50 to-indigo-50/30 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 rounded-2xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 backdrop-blur-sm w-full max-w-6xl max-h-[90vh] flex flex-col">
          
          {/* JSON Viewer Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200/50 dark:border-gray-700/50">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100">Clauses JSON Data</h3>
              {Array.isArray(clausesData?.clauses) && (
                <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded-lg text-sm font-medium">
                  {clausesData.clauses.length} clauses
                </span>
              )}
            </div>
            <button 
              onClick={() => setShowJsonViewer(false)} 
              className="btn-icon"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* JSON Content */}
          <div className="flex-1 overflow-auto p-6 modal-scrollable">
            <div className="rounded-xl p-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 overflow-auto">
              <JsonView
                data={clausesData}
                style={document.documentElement.classList.contains('dark') ? darkStyles : defaultStyles}
                shouldExpandNode={(level, value) => level < 2}
              />
            </div>
          </div>

          {/* JSON Viewer Footer */}
          <div className="flex items-center justify-between p-6 border-t border-gray-200/50 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-800/50">
            <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                JSON Format
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                {clausesData?.compliance_summary?.total_clauses || (Array.isArray(clausesData?.clauses) ? clausesData.clauses.length : 'N/A')} Total Clauses
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                Score: {clausesData?.compliance_summary?.overall_score ?? 'N/A'}%
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  try {
                    const jsonString = JSON.stringify(clausesData, null, 2);
                    const blob = new Blob([jsonString], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    const filename = `clauses-${(selectedDocument?.id || 'data')}.json`;
                    a.href = url;
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                  } catch (e) {
                    console.error('Failed to download JSON', e);
                  }
                }}
                className="btn-info-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" />
                </svg>
                Download JSON
              </button>
              <button
                onClick={() => setShowJsonViewer(false)}
                className="btn-secondary-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Upload Limit Reached Modal */}
    {showUploadLimitModal && (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
        <div className="relative bg-white dark:bg-gray-900 rounded-3xl shadow-2xl border border-gray-200 dark:border-gray-700 p-8 max-w-md w-full animate-modal-pop">
          <div className="mb-4 flex items-center justify-center">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-rose-400 text-white flex items-center justify-center shadow-lg">
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
            </div>
          </div>
          <h3 className="text-2xl font-bold text-center text-gray-900 dark:text-gray-100 mb-2">Document Limit Reached</h3>
          <p className="text-gray-600 dark:text-gray-300 text-center mb-6">
            You have reached your current document upload limit.
          </p>
          {uploadLimitContext && (
            <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3 mb-6">
              <div className="flex items-center justify-between">
                <span>Documents</span>
                <span className="font-semibold">{uploadLimitContext.documentCount}</span>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span>Limit</span>
                <span className="font-semibold">{uploadLimitContext.limit}</span>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span>Trial</span>
                <span className="font-semibold">{String(uploadLimitContext.isTrial)}</span>
              </div>
            </div>
          )}
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => setShowUploadLimitModal(false)}
              className="px-5 py-2.5 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100 font-semibold border border-gray-200 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700 transition"
            >
              Close
            </button>
            <button
              onClick={() => { setShowUploadLimitModal(false); try { window.openPaymentModal && window.openPaymentModal(); } catch(_) {} }}
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-blue-500 text-white font-semibold shadow hover:from-indigo-700 hover:to-blue-600 transition"
            >
              Upgrade to Pro
            </button>
          </div>
        </div>
        <style>{`
          @keyframes fade-in { 0% { opacity: 0; } 100% { opacity: 1; } }
          .animate-fade-in { animation: fade-in 0.4s ease; }
          @keyframes modal-pop { 0% { transform: scale(0.9); opacity: 0; } 60% { transform: scale(1.03); opacity: 1; } 100% { transform: scale(1); } }
          .animate-modal-pop { animation: modal-pop 0.45s cubic-bezier(0.68, -0.55, 0.27, 1.55); }
        `}</style>
      </div>
    )}
    </>
  );
}
